import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  COACH_HAND_SIZE,
  type CoachHandPlacement,
  coachHandPlacement,
  HAND_TRAVEL,
  handTravelAt,
  handTravelMs,
} from './coach-hand-placement';

/**
 * Main du coach : l'index pointe ce qui pulse (`[data-coach-target]`) et fait un va-et-vient, comme un doigt qui
 * tape. Elle suit l'élément à chaque image (défilement, dock qui monte) sans rendre à nouveau la page, ne capte
 * aucun toucher et n'est pas lue par le lecteur d'écran (le coach dit déjà quoi faire).
 *
 * ⚠️ Elle reste COLLÉE à sa cible (un défilement ne doit pas la laisser derrière), mais elle GLISSE quand le coach
 * change de cible : le trajet est une interpolation faite dans la boucle d'images, pas une transition CSS — une
 * transition aurait aussi traîné pendant le défilement.
 *
 * ⚠️ Le point de DÉPART du glissé est l'élément qu'elle quitte, remesuré à chaque image : le coach fait souvent
 * défiler la page vers la nouvelle cible AU MOMENT où il change (défilement doux de 300 à 600 ms), et un départ
 * figé en coordonnées d'écran laissait la main décalée du défilement pendant tout le trajet, puis sauter à la fin.
 * Si l'élément quitté a disparu (une ligne validée, un clavier refermé), elle se repose d'un pop au nouvel endroit.
 */
export function CoachHand() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const raf =
      window.requestAnimationFrame?.bind(window) ?? ((f: () => void) => window.setTimeout(f, 16));
    const cancel =
      window.cancelAnimationFrame?.bind(window) ?? ((id: number) => window.clearTimeout(id));
    // en mouvement réduit, elle se pose d'un coup : aucun glissé, aucun pop
    const calm = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let id = 0;
    let seen: HTMLElement | null = null;
    let pose: CoachHandPlacement | null = null;
    let trip: {
      fromEl: HTMLElement | null;
      from: CoachHandPlacement;
      t0: number;
      ms: number;
      enter: boolean;
    } | null = null;
    const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const poseOf = (el: HTMLElement | null) => {
      if (!el || !el.isConnected) return null;
      const rect = el.getBoundingClientRect();
      // le dock du bas (`--dock-h`) est un mur : la main se pose AU-DESSUS d'un jeton qui le frôle au lieu
      // de se dessiner par-dessus les touches. Une touche du dock, elle, garde tout l'écran pour place.
      const dock = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--dock-h'),
      );
      const floor = dock > 24 ? window.innerHeight - dock : window.innerHeight;
      return coachHandPlacement(rect, {
        width: window.innerWidth,
        height: rect.bottom <= floor ? floor : window.innerHeight,
      });
    };
    const place = () => {
      const hand = ref.current;
      if (hand) {
        const target = document.querySelector<HTMLElement>('[data-coach-target]');
        const at = poseOf(target);
        if (!at) {
          // plus rien à montrer : la prochaine apparition repart d'un pop, pas d'une vieille place
          hand.style.display = 'none';
          seen = null;
          pose = null;
          trip = null;
        } else {
          if (target !== seen) {
            // on glisse DEPUIS l'élément qu'on quitte (remesuré à chaque image), sinon on se pose d'un pop
            const fromEl = seen && seen.isConnected ? seen : null;
            const from = fromEl ? poseOf(fromEl) : null;
            trip = calm
              ? null
              : from
                ? { fromEl, from, t0: now(), ms: handTravelMs(from, at), enter: false }
                : { fromEl: null, from: at, t0: now(), ms: HAND_TRAVEL.enter, enter: true };
            seen = target;
          }
          let p = 1;
          if (trip) {
            p = trip.ms > 0 ? (now() - trip.t0) / trip.ms : 1;
            if (p >= 1) {
              p = 1;
              trip = null;
            }
          }
          const enter = !!trip?.enter;
          // le départ suit le défilement : l'élément quitté est remesuré, sa dernière place sert de repli
          const from = trip && !enter ? (poseOf(trip.fromEl) ?? trip.from) : null;
          pose = from ? handTravelAt(from, at, p) : at;
          const scale = enter ? 0.72 + 0.28 * p : 1;
          hand.style.display = 'block';
          hand.style.opacity = enter ? String(p) : '1';
          hand.style.transform =
            `translate(${pose.left}px, ${pose.top}px)` +
            (pose.flipped ? ' rotate(180deg)' : '') +
            (scale === 1 ? '' : ` scale(${scale})`);
          hand.dataset.flipped = pose.flipped ? 'true' : 'false';
          // le doigt ne tape pas en chemin : il tape là où il arrive
          hand.dataset.moving = trip && !enter ? 'true' : 'false';
          // deux touchers rapprochés puis une pause quand l'étape demande un double toucher
          hand.dataset.tap = target?.getAttribute('data-coach-target') === 'double' ? 'double' : '';
        }
      }
      id = raf(place);
    };
    place();
    return () => cancel(id);
  }, []);

  return createPortal(
    <div
      ref={ref}
      className="jr-coach-hand"
      style={{ display: 'none', width: COACH_HAND_SIZE.width, height: COACH_HAND_SIZE.height }}
      aria-hidden="true"
      data-testid="coach-hand"
    >
      <svg
        className="jr-coach-hand-tap"
        viewBox="0 0 40 48"
        width={COACH_HAND_SIZE.width}
        height={COACH_HAND_SIZE.height}
      >
        <g fill="#ffffff" stroke="#10203f" strokeWidth="2" strokeLinejoin="round">
          {/* pouce, puis paume, doigts repliés, et l'index tendu par-dessus */}
          <rect x="3" y="27" width="13" height="7" rx="3.5" transform="rotate(-28 9 30)" />
          <rect x="9" y="21" width="26" height="24" rx="9" />
          <rect x="26" y="20" width="8" height="11" rx="4" />
          <rect x="19.5" y="18" width="8" height="12" rx="4" />
          <rect x="11.5" y="1" width="9" height="29" rx="4.5" />
        </g>
      </svg>
    </div>,
    document.body,
  );
}
